import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useEffect, useMemo, useState } from 'react';
import { slugify } from '@/lib/slugify';
import dynamic from 'next/dynamic';
import { usePopup } from '@/contexts/PopupContext';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { getLocationSlugPaths } from '@/lib/locationStaticPaths';
import { getQueueBusinessData } from '@/lib/queueBusinessData';

const RequestItemFormPopup = dynamic(
    () => import('@/components/requestItem/RequestItemFormPopup'),
    { ssr: false }
);

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SLUG = 'queue-for-you-near-me';
const DEFAULT_CATEGORY_SLUG = 'licence-renewals';

const CATEGORY_MAP: Record<string, { id: string; label: string; description: string }> = {
    'licence-renewals': {
        id: '1778',
        label: 'License Renewals',
        description: 'Vehicle license disc renewals without the queue',
    },
    'change-of-ownership': {
        id: '1777',
        label: 'Change Of Ownership',
        description: 'Vehicle ownership transfer and NCO documentation',
    },
    'vehicle-registration': {
        id: '1779',
        label: 'Vehicle Registration',
        description: 'New and used vehicle registration services',
    },
    'personalised-number-plates': {
        id: '1780',
        label: 'Personalised Number Plates',
        description: 'Custom and personalised number plate applications',
    },
};

const ALL_CATEGORY_SLUGS = Object.keys(CATEGORY_MAP);

const ALL_PROVINCE_SLUGS = [
    'gauteng',
    'western-cape',
    'kwazulu-natal',
    'eastern-cape',
    'free-state',
    'mpumalanga',
    'limpopo',
    'north-west',
    'northern-cape',
];

const PROVINCE_DISPLAY_NAMES: Record<string, string> = {
    'gauteng': 'Gauteng',
    'western-cape': 'Western Cape',
    'kwazulu-natal': 'KwaZulu-Natal',
    'eastern-cape': 'Eastern Cape',
    'free-state': 'Free State',
    'mpumalanga': 'Mpumalanga',
    'limpopo': 'Limpopo',
    'north-west': 'North West',
    'northern-cape': 'Northern Cape',
};

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

const suburbLinksByCity: Record<string, string[]> = {
    Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
    Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
    'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
    Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
    Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
    Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toTitleCase(slug: string): string {
    return slug.split('-').filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface Props {
    initialBusinesses: BusinessItem[];
    initialPagination: { count: number; currentPage: number; totalPages: number };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QueueForYouSlugPage({ initialBusinesses, initialPagination }: Props) {
    const router = useRouter();

    const [{ categorySlug, provinceSlug, citySlug, suburbSlug }, setRouteData] = useState(
        { categorySlug: '', provinceSlug: '', citySlug: '', suburbSlug: '' }
    );

    useEffect(() => {
        if (!router.isReady) return;

        const pathParts = router.asPath
            .split('?')[0]
            .split('/')
            .filter(Boolean)
            .slice(1); // drop 'queue-for-you-near-me'

        // Old-format: first segment is a province slug (no category prefix)
        const isOldFormat = pathParts.length > 0 && ALL_PROVINCE_SLUGS.includes(pathParts[0]);

        if (isOldFormat) {
            setRouteData({
                categorySlug: DEFAULT_CATEGORY_SLUG,
                provinceSlug: pathParts[0] ?? '',
                citySlug: pathParts[1] ?? '',
                suburbSlug: pathParts[2] ?? '',
            });
        } else {
            // New format: /queue-for-you-near-me/[category]/[province]/[city]/[suburb]
            setRouteData({
                categorySlug: pathParts[0] ?? '',
                provinceSlug: pathParts[1] ?? '',
                citySlug: pathParts[2] ?? '',
                suburbSlug: pathParts[3] ?? '',
            });
        }
    }, [router.isReady, router.asPath]);

    // ── Derived values ──────────────────────────────────────────────────────

    const effectiveCategorySlug = (categorySlug && CATEGORY_MAP[categorySlug]) ? categorySlug : DEFAULT_CATEGORY_SLUG;
    const categoryInfo = CATEGORY_MAP[effectiveCategorySlug] ?? CATEGORY_MAP[DEFAULT_CATEGORY_SLUG];
    const categoryId = categoryInfo.id;
    const categoryLabel = categoryInfo.label;

    const provinceName = PROVINCE_DISPLAY_NAMES[provinceSlug] ?? (provinceSlug ? toTitleCase(provinceSlug) : '');
    const cityName = citySlug ? toTitleCase(citySlug) : '';
    const suburbName = suburbSlug ? toTitleCase(suburbSlug) : '';

    const hasLocation = Boolean(provinceSlug || citySlug || suburbSlug);

    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocation ? `in ${locationLabel}` : 'in South Africa';

    const baseSlugPath = `/${PAGE_SLUG}/${effectiveCategorySlug}`;

    // ── SEO ─────────────────────────────────────────────────────────────────

    const pageTitle = hasLocation
        ? `${categoryLabel} ${locationSuffix} | We Queue For You Near Me`
        : `${categoryLabel} Near Me | Vehicle Licensing Services South Africa`;

    const pageDescription = hasLocation
        ? `Find ${categoryLabel} services ${locationSuffix}. Skip the queue — professional vehicle licensing agents handle your paperwork fast.`
        : `Find ${categoryLabel} services across South Africa. We Queue For You — professional agents handle license renewals, registrations, change of ownership and more.`;

    const canonicalParts = ['', PAGE_SLUG, effectiveCategorySlug];
    if (provinceSlug) canonicalParts.push(provinceSlug);
    if (citySlug) canonicalParts.push(citySlug);
    if (suburbSlug) canonicalParts.push(suburbSlug);
    const canonicalUrl = `${ENV.DOMAIN_URL}${canonicalParts.join('/')}`;

    // ── Suburb quick links ───────────────────────────────────────────────────

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];

    // ── BusinessList searchParams ────────────────────────────────────────────
    // useMemo prevents a new object reference on every render, which would cause
    // BusinessList's useEffect (deps: [searchParams]) to fire infinitely.
    const searchParams = useMemo(() => ({
        category: '',
        province: provinceSlug,
        city: citySlug,
        suburb: suburbSlug,
        text: '',
        page: 1,
    }), [provinceSlug, citySlug, suburbSlug]);

    // ── RFQ popup ───────────────────────────────────────────────────────────
    const { isPopupOpen, popupType, resetPopup, setPopupOpen, setPopupType } = usePopup();
    const handleOpenRFQ = () => {
        setPopupType('carousel');
        setPopupOpen(true);
    };

    // ── FAQ ─────────────────────────────────────────────────────────────────

    const faqItems = [
        {
            question: `Where can I find ${categoryLabel} services ${locationSuffix}?`,
            answer: hasLocation
                ? `Browse ${categoryLabel} service providers ${locationSuffix} using the listing above. Use the filters to narrow by city or suburb.`
                : `Use the province and city filters to find ${categoryLabel} agents near you across South Africa.`,
        },
        {
            question: 'What is the We Queue For You service?',
            answer: 'We Queue For You is a professional vehicle licensing and registration queue service. Agents handle all your paperwork and queuing at licensing departments — you just drop off your documents and collect the results.',
        },
        {
            question: 'Which vehicle services can be handled for me?',
            answer: 'Services include license disc renewals, vehicle registration, change of ownership (NCO), personalised number plates, de-registration, traffic fines, driver\'s licenses, and more.',
        },
        {
            question: 'How long does the service take?',
            answer: 'Processing times vary by service type and department. Agents typically complete renewals within 1–5 business days. Contact a provider directly for specific turnaround estimates.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'BreadcrumbList',
                                    itemListElement: [
                                        { '@type': 'ListItem', position: 1, name: 'We Queue For You', item: `${ENV.DOMAIN_URL}/we-queue-for-you` },
                                        { '@type': 'ListItem', position: 2, name: categoryLabel, item: `${ENV.DOMAIN_URL}/${PAGE_SLUG}/${effectiveCategorySlug}` },
                                        ...(provinceSlug ? [{ '@type': 'ListItem', position: 3, name: provinceName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}` }] : []),
                                        ...(citySlug ? [{ '@type': 'ListItem', position: 4, name: cityName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}/${citySlug}` }] : []),
                                        ...(suburbSlug ? [{ '@type': 'ListItem', position: 5, name: suburbName, item: `${ENV.DOMAIN_URL}${baseSlugPath}/${provinceSlug}/${citySlug}/${suburbSlug}` }] : []),
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: { '@type': 'Answer', text: item.answer },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>

            {/* ── Hero ──────────────────────────────────────────────────────── */}
            <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
                <div className="absolute inset-0 bg-black/20" />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            {/* Breadcrumb */}
                            <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm text-blue-200">
                                <Link href="/we-queue-for-you" className="hover:text-white">We Queue For You</Link>
                                <span className="opacity-50">&rsaquo;</span>
                                <Link href={`/${PAGE_SLUG}`} className="hover:text-white">Near Me</Link>
                                {effectiveCategorySlug && (
                                    <>
                                        <span className="opacity-50">&rsaquo;</span>
                                        <Link href={`/${PAGE_SLUG}/${effectiveCategorySlug}`} className="hover:text-white">{categoryLabel}</Link>
                                    </>
                                )}
                                {provinceSlug && (
                                    <>
                                        <span className="opacity-50">&rsaquo;</span>
                                        <Link href={`${baseSlugPath}/${provinceSlug}`} className="hover:text-white">{provinceName}</Link>
                                    </>
                                )}
                                {citySlug && (
                                    <>
                                        <span className="opacity-50">&rsaquo;</span>
                                        <Link href={`${baseSlugPath}/${provinceSlug}/${citySlug}`} className="hover:text-white">{cityName}</Link>
                                    </>
                                )}
                                {suburbSlug && (
                                    <>
                                        <span className="opacity-50">&rsaquo;</span>
                                        <span className="text-white">{suburbName}</span>
                                    </>
                                )}
                            </nav>

                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300">Vehicle Licensing Services</p>
                            <h1 className="mt-3 text-3xl font-bold leading-tight md:text-5xl">
                                {categoryLabel} {locationSuffix}
                            </h1>
                            <p className="mt-4 text-base text-blue-100 md:text-lg">
                                {categoryInfo.description}. Skip the queue — let professionals handle your vehicle paperwork.
                            </p>

                            {/* Category tabs */}
                            <div className="mt-6 flex flex-wrap gap-2">
                                {ALL_CATEGORY_SLUGS.map((slug) => {
                                    const cat = CATEGORY_MAP[slug];
                                    const isActive = slug === effectiveCategorySlug;
                                    const targetPath = provinceSlug
                                        ? `/${PAGE_SLUG}/${slug}/${provinceSlug}${citySlug ? `/${citySlug}` : ''}${suburbSlug ? `/${suburbSlug}` : ''}`
                                        : `/${PAGE_SLUG}/${slug}`;
                                    return (
                                        <Link
                                            key={slug}
                                            href={targetPath}
                                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                                isActive
                                                    ? 'bg-yellow-400 text-blue-900'
                                                    : 'border border-white/30 text-white hover:border-yellow-300 hover:text-yellow-200'
                                            }`}
                                        >
                                            {cat.label}
                                        </Link>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={handleOpenRFQ}
                                className="mt-6 rounded-xl bg-yellow-400 px-6 py-3 text-sm font-bold text-blue-900 hover:bg-yellow-300"
                            >
                                Request a Quote for {categoryLabel}
                            </button>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <p className="text-sm font-semibold text-blue-100">Quick searches</p>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link href={`${baseSlugPath}/gauteng/johannesburg`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300">
                                        {categoryLabel} Johannesburg
                                    </Link>
                                    <Link href={`${baseSlugPath}/western-cape/cape-town`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300">
                                        {categoryLabel} Cape Town
                                    </Link>
                                    <Link href={`${baseSlugPath}/kwazulu-natal/durban`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300">
                                        {categoryLabel} Durban
                                    </Link>
                                    <Link href={`${baseSlugPath}/gauteng/pretoria`} className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300">
                                        {categoryLabel} Pretoria
                                    </Link>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {quickProvinces.map((p) => (
                                        <Link
                                            key={p}
                                            href={`${baseSlugPath}/${slugify(p)}`}
                                            className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-blue-100 hover:border-yellow-300 hover:text-yellow-200"
                                        >
                                            {p}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Business Listings ─────────────────────────────────────────── */}
            <div className="my-10" id="business-filters">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="1774,1775,1777,1778,1779,1780"
                        initialBusinesses={initialBusinesses}
                        initialPagination={initialPagination}
                        skipAuth={true}
                    />
                </div>
            </div>

            {/* ── City links ────────────────────────────────────────────────── */}
            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `${categoryLabel} by Suburb in ${cityName}`
                            : `${categoryLabel} by City`}
                    </h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`${baseSlugPath}/${provinceSlug}/${citySlug}/${slugify(suburb)}`}
                                      className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:border-blue-500 hover:text-blue-700"
                                  >
                                      {`${categoryLabel} in ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`${baseSlugPath}/${city.province}/${slugify(city.name)}`}
                                      className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:border-blue-500 hover:text-blue-700"
                                  >
                                      {`${categoryLabel} in ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            {/* ── Other categories ──────────────────────────────────────────── */}
            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">Other Vehicle Licensing Services</h2>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {ALL_CATEGORY_SLUGS.filter((s) => s !== effectiveCategorySlug).map((slug) => {
                            const cat = CATEGORY_MAP[slug];
                            const targetPath = provinceSlug
                                ? `/${PAGE_SLUG}/${slug}/${provinceSlug}${citySlug ? `/${citySlug}` : ''}`
                                : `/${PAGE_SLUG}/${slug}`;
                            return (
                                <Link
                                    key={slug}
                                    href={targetPath}
                                    className="rounded-xl border border-slate-200 p-4 hover:border-blue-400 hover:shadow-sm"
                                >
                                    <p className="text-sm font-semibold text-slate-900">{cat.label}</p>
                                    <p className="mt-1 text-xs text-slate-500">{cat.description}</p>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── FAQ ───────────────────────────────────────────────────────── */}
            <section className="container my-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-slate-900">Frequently Asked Questions</h2>
                    <div className="mt-4 space-y-4">
                        {faqItems.map((item) => (
                            <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <DownloadApp />

            <RequestItemFormPopup
                currentPopupType={popupType}
                isContextOpen={isPopupOpen}
                disableAutoOpen={true}
                onClose={resetPopup}
                initialData={{
                    title: categoryLabel,
                    description: `Request for ${categoryLabel} - We Queue For You`,
                    categoryId: categoryId,
                }}
            />
        </>
    );
}

const QUICK_CITY_SLUGS = [
    { province: 'gauteng', city: 'johannesburg' },
    { province: 'gauteng', city: 'pretoria' },
    { province: 'western-cape', city: 'cape-town' },
    { province: 'kwazulu-natal', city: 'durban' },
    { province: 'eastern-cape', city: 'gqeberha' },
    { province: 'free-state', city: 'bloemfontein' },
    { province: 'mpumalanga', city: 'nelspruit' },
    { province: 'limpopo', city: 'polokwane' },
    { province: 'north-west', city: 'klerksdorp' },
    { province: 'northern-cape', city: 'kimberley' },
];

export const getStaticPaths: GetStaticPaths = async () => {
    const locationPaths = getLocationSlugPaths(3);

    const paths = [
        // Shell placeholder
        { params: { slug: ['sss-shell'] } },
        // Category-only: /queue-for-you-near-me/[category]
        ...ALL_CATEGORY_SLUGS.map((cat) => ({ params: { slug: [cat] } })),
        // Category + province/city/suburb: /queue-for-you-near-me/[category]/[province]/[city?]/[suburb?]
        ...ALL_CATEGORY_SLUGS.flatMap((cat) =>
            locationPaths.map((loc) => ({ params: { slug: [cat, ...loc] } }))
        ),
        // Old-format (no category prefix): /queue-for-you-near-me/[province]/[city?]/[suburb?]
        ...locationPaths.map((loc) => ({ params: { slug: loc } })),
    ];

    return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const slugArr = (params?.slug as string[]) ?? [];
    const firstSeg = slugArr[0] ?? '';

    // Shell placeholder — no fetch needed
    if (firstSeg === 'sss-shell') {
        return {
            props: {
                initialBusinesses: [],
                initialPagination: { count: 0, currentPage: 1, totalPages: 0 },
            },
        };
    }

    const businessData = await getQueueBusinessData();

    const isStaticExport = process.env.BUILD_MODE === 'export';
    return {
        props: {
            initialBusinesses: businessData.result,
            initialPagination: {
                count: businessData.count,
                currentPage: businessData.currentPage,
                totalPages: businessData.totalPages,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
